// ============================================================================
// USER MANAGEMENT SERVICE - frontend/src/services/userManagement.ts
// API calls for user management (system admin only)
// ============================================================================

import { api } from './api';

export type UserRole = 'normal' | 'read_only' | 'avatar_admin' | 'template_admin' | 'compliance_user' | 'compliance_admin' | 'system_admin' | 'project_manager' | 'workflow_admin' | 'compliance_officer';

export interface UserProfile {
  userId: string;
  cognitoSub: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  organizationId: string;
  role: UserRole;
  status: 'active' | 'suspended' | 'deleted';
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  lastLoginAt?: string;
  enabledFeatures?: string[];
}

export interface CreateUserInput {
  email: string;
  firstName: string;
  lastName: string;
  organizationId: string;
  role: UserRole;
  temporaryPassword?: string;
}

export interface UpdateUserInput {
  firstName?: string;
  lastName?: string;
  organizationId?: string;
  role?: UserRole;
  status?: 'active' | 'suspended';
  enabledFeatures?: string[];
}

/**
 * List all users with optional filters
 */
export async function listUsers(filters?: {
  organizationId?: string;
  role?: string;
  status?: string;
  search?: string;
}): Promise<{ users: UserProfile[]; total: number }> {
  const params = new URLSearchParams();
  if (filters?.organizationId) params.append('organizationId', filters.organizationId);
  if (filters?.role) params.append('role', filters.role);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.search) params.append('search', filters.search);

  const response = await api.get(`/api/admin/users?${params.toString()}`);
  return response.data;
}

/**
 * Get user details by ID
 */
export async function getUser(userId: string): Promise<UserProfile> {
  const response = await api.get(`/api/admin/users/${userId}`);
  return response.data.user;
}

/**
 * Create a new user
 */
export async function createUser(input: CreateUserInput): Promise<{ user: UserProfile; temporaryPassword: string }> {
  const response = await api.post('/api/admin/users', input);
  return { user: response.data.user, temporaryPassword: response.data.temporaryPassword };
}

/**
 * Update user details
 */
export async function updateUser(userId: string, updates: UpdateUserInput): Promise<UserProfile> {
  const response = await api.put(`/api/admin/users/${userId}`, updates);
  return response.data.user;
}

/**
 * Update user role
 */
export async function updateUserRole(
  userId: string,
  role: UserRole
): Promise<UserProfile> {
  const response = await api.patch(`/api/admin/users/${userId}/role`, { role });
  return response.data.user;
}

/**
 * Suspend user account
 */
export async function suspendUser(userId: string, reason?: string): Promise<void> {
  await api.post(`/api/admin/users/${userId}/suspend`, { reason });
}

/**
 * Activate user account
 */
export async function activateUser(userId: string): Promise<void> {
  await api.post(`/api/admin/users/${userId}/activate`);
}

/**
 * Delete user (soft delete)
 */
export async function deleteUser(userId: string): Promise<void> {
  await api.delete(`/api/admin/users/${userId}`);
}

/**
 * Reset user password
 */
export async function resetUserPassword(userId: string): Promise<{ temporaryPassword: string }> {
  const response = await api.post(`/api/admin/users/${userId}/reset-password`);
  return response.data;
}
